import { Flex, IconButton, Select, Tag, Text } from '@chakra-ui/react';
import KeyboardArrowLeftOutlinedIcon from '@mui/icons-material/KeyboardArrowLeftOutlined';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import KeyboardDoubleArrowLeftOutlinedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftOutlined';
import KeyboardDoubleArrowRightOutlinedIcon from '@mui/icons-material/KeyboardDoubleArrowRightOutlined';

export interface GridPaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalRows: number;
  pageSizeOptions?: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * Custom pagination component for GridTable with Indonesian locale
 * Displays "Halaman X dari Y" and "per halaman" selector
 */
export function GridPagination({
  currentPage,
  totalPages,
  pageSize,
  totalRows,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onPageChange,
  onPageSizeChange,
}: GridPaginationProps) {
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages || totalPages === 0;

  const handleFirstPage = () => onPageChange(1);
  const handlePreviousPage = () => onPageChange(currentPage - 1);
  const handleNextPage = () => onPageChange(currentPage + 1);
  const handleLastPage = () => onPageChange(totalPages);

  return (
    <Flex
      justify="center"
      align="center"
      gap={4}
      py={4}
      borderTop="1px solid"
      borderColor="gray.200"
    >
      <Flex align="center" gap={2}>
        <IconButton
          aria-label="Halaman pertama"
          icon={<KeyboardDoubleArrowLeftOutlinedIcon />}
          size="sm"
          variant="outline"
          borderColor="gray.33"
          onClick={handleFirstPage}
          isDisabled={isFirstPage}
        />
        <IconButton
          aria-label="Halaman sebelumnya"
          icon={<KeyboardArrowLeftOutlinedIcon />}
          size="sm"
          variant="outline"
          borderColor="gray.33"
          onClick={handlePreviousPage}
          isDisabled={isFirstPage}
        />

        <Flex
          align="center"
          gap={2}
          px={4}
          py={2}
          borderWidth="1px"
          borderRadius="md"
          borderColor="gray.33"
          fontSize="14px"
        >
          <Text>Halaman</Text>
          <Tag bgColor="blue" textColor="white" size="sm">
            {totalPages > 0 ? currentPage : 0}
          </Tag>
          <Text>dari</Text>
          <Text fontWeight="bold">{totalPages}</Text>
        </Flex>

        <IconButton
          aria-label="Halaman berikutnya"
          icon={<KeyboardArrowRightOutlinedIcon />}
          size="sm"
          variant="outline"
          borderColor="gray.33"
          onClick={handleNextPage}
          isDisabled={isLastPage}
        />
        <IconButton
          aria-label="Halaman terakhir"
          icon={<KeyboardDoubleArrowRightOutlinedIcon />}
          size="sm"
          variant="outline"
          borderColor="gray.33"
          onClick={handleLastPage}
          isDisabled={isLastPage}
        />
      </Flex>

      <Flex align="center" gap={2} fontSize="14px">
        <Text>per halaman</Text>
        <Select
          size="sm"
          width="80px"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          borderColor="gray.33"
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </Select>
      </Flex>
    </Flex>
  );
}

export default GridPagination;
